import React, { useCallback, useEffect, useState, RefObject } from 'react';
import { fetchProtectedMedia } from '../../../utils/protectedMedia';
import { useZoomNativeConfig } from '../../../hoc/withZoomViewer';

interface ImageContainerProps {
  imageUrl: string;
  imageRef: RefObject<HTMLImageElement | null>;
  setImageMeasures: React.Dispatch<React.SetStateAction<{ width: number; height: number }>>;
  canvasMeasures: {
    width: number;
    height: number;
  };
  onLoadError?: (hasError: boolean) => void;
}

export const ImageContainer: React.FC<ImageContainerProps> = ({
  imageUrl,
  imageRef,
  setImageMeasures,
  canvasMeasures,
  onLoadError,
}) => {
  const { viewScale } = useZoomNativeConfig();
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false);
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    let nextObjectUrl: string | null = null;
    let cancelled = false;

    const fail = (err: unknown, message: string) => {
      if (cancelled) return;

      console.error(message, err);
      if (imageRef) {
        imageRef.current = null;
      }
      setObjectUrl(null);
      setHasError(true);
      onLoadError?.(true);
    };

    const loadImage = async () => {
      setHasError(false);
      onLoadError?.(false);

      try {
        const response = await fetchProtectedMedia(imageUrl);

        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.status}`);
        }

        const blob = await response.blob();
        nextObjectUrl = URL.createObjectURL(blob);

        const imageToLoad = new window.Image();

        imageToLoad.onload = () => {
          if (cancelled) return;

          const actualSize = {
            width: imageToLoad.naturalWidth,
            height: imageToLoad.naturalHeight,
          };

          if (imageRef) {
            imageRef.current = imageToLoad;
          }
          setObjectUrl(nextObjectUrl);
          setHasError(false);
          onLoadError?.(false);
          setImageMeasures(previous =>
            previous.width === actualSize.width && previous.height === actualSize.height ? previous : actualSize
          );
        };

        imageToLoad.onerror = err => {
          fail(err, 'Image load failed');
        };

        imageToLoad.src = nextObjectUrl;
      } catch (err) {
        fail(err, 'Fetch image failed');
      }
    };

    loadImage();

    return () => {
      cancelled = true;
      if (nextObjectUrl) {
        URL.revokeObjectURL(nextObjectUrl);
      }
    };
  }, [imageUrl, imageRef, retryToken, setImageMeasures, onLoadError]);

  const handleRetry = useCallback(() => {
    setRetryToken(token => token + 1);
  }, []);

  const layoutWidth = canvasMeasures.width;
  const layoutHeight = canvasMeasures.height;
  const paintScale = viewScale > 0 ? viewScale : 1;
  const paintWidth = Math.max(1, Math.round(layoutWidth * paintScale));
  const paintHeight = Math.max(1, Math.round(layoutHeight * paintScale));

  if (hasError) {
    return (
      <button
        type="button"
        onClick={handleRetry}
        style={{
          width: layoutWidth || '100%',
          height: layoutHeight || '100%',
          border: 0,
          background: 'transparent',
          color: '#ffffff',
          fontSize: 16,
          cursor: 'pointer',
        }}>
        Failed to load image. Click to retry.
      </button>
    );
  }

  if (!objectUrl || !layoutWidth || !layoutHeight) {
    return null;
  }

  return (
    <img
      src={objectUrl}
      alt=""
      width={paintWidth}
      height={paintHeight}
      draggable={false}
      decoding="async"
      style={{
        display: 'block',
        width: paintWidth,
        height: paintHeight,
        maxWidth: 'none',
        pointerEvents: 'none',
        userSelect: 'none',
        imageRendering: 'auto',
        transform: paintScale === 1 ? undefined : `scale(${1 / paintScale})`,
        transformOrigin: '0 0',
      }}
    />
  );
};
